import React, { useState, useEffect, useCallback } from 'react';
import { X, Printer, CheckCircle, Clock, AlertCircle, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Global event name for print status updates
export const PRINT_STATUS_EVENT = 'print-status-update';

// Helper function to dispatch print status events from anywhere in the app
export const dispatchPrintStatus = (status) => {
  const event = new CustomEvent(PRINT_STATUS_EVENT, { detail: status });
  window.dispatchEvent(event);
};

// Status icons mapping
const StatusIcon = ({ status }) => {
  switch (status) {
    case 'pending':
      return <Clock className="w-4 h-4 text-yellow-500" />;
    case 'printing':
      return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
    case 'completed':
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    case 'failed':
      return <AlertCircle className="w-4 h-4 text-red-500" />;
    default:
      return <Clock className="w-4 h-4 text-gray-400" />;
  }
};

// Status color mapping
const getStatusColor = (status) => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-50 border-yellow-200';
    case 'printing':
      return 'bg-blue-50 border-blue-200';
    case 'completed':
      return 'bg-green-50 border-green-200';
    case 'failed':
      return 'bg-red-50 border-red-200';
    default:
      return 'bg-gray-50 border-gray-200';
  }
};

const PrintStatusPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [printJobs, setPrintJobs] = useState([]);
  const [autoCloseTimer, setAutoCloseTimer] = useState(null);

  // Listen for print status events
  useEffect(() => {
    const handlePrintStatus = (event) => {
      const { type, job } = event.detail;
      
      if (type === 'add') {
        // Add new job
        setPrintJobs(prev => [{
          id: job.id || Date.now(),
          partNo: job.partNo || 'Unknown',
          partName: job.partName || '',
          copies: job.copies || 1,
          size: job.size || 'big',
          status: 'pending',
          timestamp: new Date(),
          partyCode: job.partyCode || '',
        }, ...prev].slice(0, 10)); // Keep only last 10 jobs
        
        setIsOpen(true);
        setIsMinimized(false);
        
        // Clear any existing auto-close timer
        if (autoCloseTimer) clearTimeout(autoCloseTimer);
        
        // Simulate status progression
        setTimeout(() => {
          setPrintJobs(prev => prev.map(j => 
            j.id === (job.id || Date.now()) ? { ...j, status: 'printing' } : j
          ));
        }, 500);
        
        setTimeout(() => {
          setPrintJobs(prev => prev.map(j => 
            j.id === (job.id || Date.now()) ? { ...j, status: 'completed' } : j
          ));
          
          // Auto-minimize after all jobs complete
          const timer = setTimeout(() => {
            setIsMinimized(true);
          }, 3000);
          setAutoCloseTimer(timer);
        }, 2000);
      } else if (type === 'batch') {
        // Add multiple jobs at once
        const newJobs = (job.items || []).map((item, idx) => ({
          id: Date.now() + idx,
          partNo: item.partNo || 'Unknown',
          partName: item.partName || item.description || '',
          copies: item.copies || 1,
          size: item.size || 'big',
          status: 'pending',
          timestamp: new Date(),
          partyCode: item.partyCode || '',
        }));
        
        setPrintJobs(prev => [...newJobs, ...prev].slice(0, 20));
        setIsOpen(true);
        setIsMinimized(false);
        
        if (autoCloseTimer) clearTimeout(autoCloseTimer);
        
        // Simulate batch processing
        newJobs.forEach((newJob, idx) => {
          setTimeout(() => {
            setPrintJobs(prev => prev.map(j => 
              j.id === newJob.id ? { ...j, status: 'printing' } : j
            ));
          }, 300 + idx * 200);
          
          setTimeout(() => {
            setPrintJobs(prev => prev.map(j => 
              j.id === newJob.id ? { ...j, status: 'completed' } : j
            ));
          }, 1500 + idx * 300);
        });
        
        // Auto-minimize after all complete
        const totalTime = 2000 + newJobs.length * 300;
        const timer = setTimeout(() => {
          setIsMinimized(true);
        }, totalTime + 2000);
        setAutoCloseTimer(timer);
      } else if (type === 'error') {
        // Mark job as failed
        setPrintJobs(prev => prev.map(j => 
          j.id === job.id ? { ...j, status: 'failed', error: job.error } : j
        ));
      }
    };

    window.addEventListener(PRINT_STATUS_EVENT, handlePrintStatus);
    return () => {
      window.removeEventListener(PRINT_STATUS_EVENT, handlePrintStatus);
      if (autoCloseTimer) clearTimeout(autoCloseTimer);
    };
  }, [autoCloseTimer]);

  // Clear completed jobs
  const clearCompleted = useCallback(() => {
    setPrintJobs(prev => prev.filter(j => j.status !== 'completed'));
  }, []);

  // Close panel
  const handleClose = () => {
    setIsOpen(false);
    setPrintJobs([]);
  };

  if (!isOpen || printJobs.length === 0) return null;

  const pendingCount = printJobs.filter(j => j.status === 'pending' || j.status === 'printing').length;
  const completedCount = printJobs.filter(j => j.status === 'completed').length;

  return (
    <div 
      className={`fixed right-4 top-20 z-50 transition-all duration-300 ${
        isMinimized ? 'w-auto' : 'w-80'
      }`}
      data-testid="print-status-panel"
    >
      {isMinimized ? (
        // Minimized view - just a small badge
        <button
          onClick={() => setIsMinimized(false)}
          className="flex items-center gap-2 bg-white border-2 border-green-500 rounded-full px-4 py-2 shadow-lg hover:shadow-xl transition-shadow"
        >
          <Printer className="w-5 h-5 text-green-600" />
          <span className="font-medium text-green-700">
            {completedCount} printed
          </span>
          {pendingCount > 0 && (
            <span className="bg-yellow-100 text-yellow-700 text-xs px-2 py-0.5 rounded-full">
              {pendingCount} pending
            </span>
          )}
          <ChevronDown className="w-4 h-4 text-gray-400" />
        </button>
      ) : (
        // Expanded view
        <div className="bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Printer className="w-5 h-5" />
              <span className="font-semibold">Print Queue</span>
              <span className="bg-white/20 text-xs px-2 py-0.5 rounded-full">
                {printJobs.length} job{printJobs.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsMinimized(true)}
                className="p-1 hover:bg-white/20 rounded transition-colors"
                title="Minimize"
              >
                <ChevronUp className="w-4 h-4" />
              </button>
              <button
                onClick={handleClose}
                className="p-1 hover:bg-white/20 rounded transition-colors"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Job List */}
          <div className="max-h-80 overflow-y-auto">
            {printJobs.map((job) => (
              <div
                key={job.id}
                className={`px-4 py-3 border-b border-gray-100 ${getStatusColor(job.status)} transition-colors`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <StatusIcon status={job.status} />
                      <span className="font-mono font-bold text-sm text-gray-900 truncate">
                        {job.partNo}
                      </span>
                      {job.partyCode && (
                        <span className="bg-gray-800 text-white text-xs px-1.5 py-0.5 rounded">
                          {job.partyCode}
                        </span>
                      )}
                    </div>
                    {job.partName && (
                      <p className="text-xs text-gray-600 truncate mt-0.5">
                        {job.partName}
                      </p>
                    )}
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span>{job.copies} {job.copies === 1 ? 'copy' : 'copies'}</span>
                      <span className="capitalize">{job.size} sticker</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs font-medium capitalize ${
                      job.status === 'completed' ? 'text-green-600' :
                      job.status === 'failed' ? 'text-red-600' :
                      job.status === 'printing' ? 'text-blue-600' :
                      'text-yellow-600'
                    }`}>
                      {job.status}
                    </span>
                  </div>
                </div>
                {job.error && (
                  <p className="text-xs text-red-600 mt-1">{job.error}</p>
                )}
              </div>
            ))}
          </div>

          {/* Footer */}
          {completedCount > 0 && (
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
              <span className="text-xs text-gray-500">
                {completedCount} completed, {pendingCount} pending
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={clearCompleted}
                className="text-xs h-7"
              >
                Clear completed
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PrintStatusPanel;
